/**
 * AP-731 cascade. Pure: given every worksheet row and the divergence snapshot
 * taken immediately before the reader's action (§1), returns the values to
 * write. Writes only to rows that were converged in the snapshot, and the
 * cascade stops at a diverged row: it holds its value, so nothing below it
 * is reached through it (R14).
 */

import type { MeasurementValueSource } from '@/models/report';
import { evaluate } from './formulaParser';
import type { DivergenceState } from './divergence';

export interface EngineRow {
  /** canonicalMeasurementKey — the identity formulas reference. */
  slug: string;
  /** The row's own stored value (the SR/manual arm). */
  value: number | null;
  valueSource?: MeasurementValueSource | null;
  hasHumanAnnotation?: boolean;
  /** The display AI variant's value, if any. */
  aiValue?: number | null;
  /** Present ⇒ engine-owned (R3). */
  formula?: string | null;
  precision: number;
}

export interface RecalculateArgs {
  rows: EngineRow[];
  /** Divergence per slug before the action; absent ⇒ treated as converged. */
  snapshot: Map<string, DivergenceState>;
  /** R17: the action was a deletion, so a converged row whose formula can no
   *  longer be evaluated is CLEARED rather than left holding a stale value. */
  clearUnresolvable?: boolean;
  /** R5: the rows the reader acted on. The action never rewrites them, and
   *  only rows downstream of them are recalculated. */
  protectedSlugs?: ReadonlySet<string>;
}

/** slug → the value to write. null clears the row (R7). */
export type RecalculateResult = Map<string, number | null>;

const warnedBadFormulas = new Set<string>();

/** R8 — manual outranks AI outranks SR. Mirrors resolveMeasurementRows: a
 *  cleared MANUAL row resolves as missing, never back to its AI reading. */
function resolve(row: EngineRow): number | null {
  const isManual = row.valueSource === 'MANUAL' || !!row.hasHumanAnnotation;
  if (isManual) return row.value;
  return row.aiValue ?? row.value;
}

function agrees(a: number, b: number, precision: number): boolean {
  return Number(a.toFixed(precision)) === Number(b.toFixed(precision));
}

/** Operand keys, as the parser reads them; a malformed formula has none. */
function operandsOf(formula: string): string[] {
  try {
    const result = evaluate(formula, () => undefined);
    return 'missing' in result ? result.missing : [];
  } catch {
    return [];
  }
}

/** R5 — computed rows that reference an origin, directly or through each
 *  other. R14 — a diverged row is a dead end: it holds its value, so nothing
 *  is reached through it. */
function downstreamOf(
  owned: EngineRow[],
  origins: ReadonlySet<string>,
  snapshot: Map<string, DivergenceState>
): Set<string> {
  const operands = new Map(
    owned.map((r) => [r.slug, operandsOf(r.formula as string)])
  );
  const reached = new Set(origins);
  const downstream = new Set<string>();
  const nextBatch = () =>
    owned.filter(
      (r) =>
        !downstream.has(r.slug) &&
        operands.get(r.slug)!.some((k) => reached.has(k))
    );
  for (let batch = nextBatch(); batch.length; batch = nextBatch()) {
    batch.forEach((r) => {
      downstream.add(r.slug);
      if (snapshot.get(r.slug) !== 'diverged') reached.add(r.slug);
    });
  }
  return downstream;
}

export function recalculate({
  rows,
  snapshot,
  clearUnresolvable = false,
  protectedSlugs,
}: RecalculateArgs): RecalculateResult {
  const writes: RecalculateResult = new Map();

  // Resolved value per slug, updated in place as the cascade writes — an
  // engine write is manual (R6), so it immediately outranks AI here too.
  const current = new Map<string, number | null>();
  rows.forEach((r) => current.set(r.slug, resolve(r)));

  const owned = rows.filter((r) => r.formula);
  const scope = protectedSlugs
    ? downstreamOf(owned, protectedSlugs, snapshot)
    : null;
  // Acyclic, so one pass per dependency link suffices; the bound is a
  // backstop against a library defect, never a cycle check we rely on.
  for (let pass = 0; pass <= owned.length; pass += 1) {
    let changed = false;

    owned.forEach((row) => {
      // R14 — a row diverged before the action is never written to.
      if (snapshot.get(row.slug) === 'diverged') return;
      // R5 — the cascade never rewrites a row the action originated at.
      if (protectedSlugs?.has(row.slug)) return;
      // R5 — rows the action does not reach are left as they are.
      if (scope && !scope.has(row.slug)) return;

      let result;
      try {
        result = evaluate(row.formula as string, (key) => {
          const v = current.get(key);
          return v === null ? undefined : v;
        });
      } catch (err) {
        if (!warnedBadFormulas.has(row.formula as string)) {
          warnedBadFormulas.add(row.formula as string);
          // eslint-disable-next-line no-console
          console.warn(`[AP-731] Malformed formula: ${row.formula}`, err);
        }
        return;
      }

      const held = current.get(row.slug) ?? null;

      if ('missing' in result) {
        // R16 keeps the value; R17's deletion is the one case that clears it.
        if (clearUnresolvable && held !== null) {
          current.set(row.slug, null);
          writes.set(row.slug, null);
          changed = true;
        }
        return;
      }

      if (held !== null && agrees(held, result.value, row.precision)) return;

      current.set(row.slug, result.value);
      writes.set(row.slug, result.value);
      changed = true;
    });

    if (!changed) break;
  }

  return writes;
}
